import { type FC } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useGetUserProfileSuspense } from '@/api/user-dashboard/queries';
import { Tabs } from '@/design-system';
import { FeatureFlags, useFeatureFlag } from '@/global-hooks/use-feature-flag-enabled';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { InvestingBoard } from '@/pages/investing-board/investing-board';
import { useIsAccreditationGated } from '@/shared/modules/accredited-investor-gate';
import { Permission, useCan } from '@/shared/modules/permissions';
import { UserSuiWalletStatus } from '@/shared/modules/wallet/use-privy-wallet';
import { useUserSuiWallet } from '@/shared/modules/wallet/use-user-sui-wallet';
import { useSelectedUserConfigSlice } from '@/stores/selectors';

import BidsEndAsks from './modules/bids-end-asks';
import MyAssets from './modules/my-assets/my-assets';
import MyTransactions from './modules/my-transactions/my-transactions';
import Opportunities from './modules/opportunities';
import { useWalletPortfolioSync } from './modules/wallet-sync/use-wallet-portfolio-sync';
import {
  PortfolioRoot,
  PrimaryTab,
  PrimaryTabsList,
  PrimaryTabsScroller,
} from './portfolio.styled';

export const portfolioTabs = {
  assets: 'my-assets',
  transactions: 'my-transactions',
  bidsAsks: 'bids-and-asks',
  opportunities: 'opportunities',
  investingBoard: 'investing-board',
};

type PortfolioTab = {
  key: string;
  label: string;
  component: FC;
  disabled?: boolean;
};

const myPortfolioTabs: PortfolioTab[] = [
  { key: portfolioTabs.assets, label: 'My Assets', component: MyAssets },
  { key: portfolioTabs.transactions, label: 'My Transactions', component: MyTransactions },
  { key: portfolioTabs.bidsAsks, label: 'Bids & Asks', component: BidsEndAsks, disabled: true },
  { key: portfolioTabs.opportunities, label: 'Opportunities', component: Opportunities },
];

const investingBoardTab: PortfolioTab = {
  key: portfolioTabs.investingBoard,
  label: 'Investing board',
  component: InvestingBoard,
};

const Portfolio = () => {
  const isMobile = useIsMobile();
  const { user } = useGetUserProfileSuspense();
  const isAccreditationGated = useIsAccreditationGated();
  const isCryptoWalletEnabled = useFeatureFlag(FeatureFlags.CRYPTO_WALLET) === true;
  const wallet = useUserSuiWallet();
  const { selectedUserRole } = useSelectedUserConfigSlice();
  const walletViewsEnabled =
    selectedUserRole === 'user' &&
    isCryptoWalletEnabled &&
    wallet.status === UserSuiWalletStatus.READY;
  const { refresh, isRefreshing } = useWalletPortfolioSync({
    enabled: walletViewsEnabled,
  });
  const isInvestingFlowEnabled = useFeatureFlag(FeatureFlags.INVESTING_FLOW);
  const canViewEntityInvestmentBoard = useCan(Permission.EntityInvestmentBoard);
  const [searchParams, setSearchParams] = useSearchParams();

  const showsInvestingBoard =
    isInvestingFlowEnabled &&
    (selectedUserRole !== 'entity' || canViewEntityInvestmentBoard) &&
    selectedUserRole !== 'admin';
  const tabs = showsInvestingBoard ? [...myPortfolioTabs, investingBoardTab] : myPortfolioTabs;

  const requestedTabKey = searchParams.get('tab');
  const currentTabKey =
    requestedTabKey && tabs.some((tab) => tab.key === requestedTabKey)
      ? requestedTabKey
      : tabs[0].key;
  const handleTabChange = (key: string | number) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', String(key));
    setSearchParams(next, { replace: true });
  };

  return (
    <PortfolioRoot
      value={currentTabKey}
      onValueChange={handleTabChange}
      size={isMobile ? 'lg' : 'sm'}
      variant="boxed"
    >
      <PrimaryTabsScroller data-testid="portfolio-tabs-scroller">
        <PrimaryTabsList>
          {tabs.map((tab) => (
            <PrimaryTab
              key={tab.key}
              value={tab.key}
              label={tab.label}
              disabled={
                tab.disabled ||
                (tab.key === portfolioTabs.opportunities &&
                  (user.isBlocked || isAccreditationGated))
              }
            />
          ))}
        </PrimaryTabsList>
      </PrimaryTabsScroller>

      {tabs.map((tab) => (
        <Tabs.Panel key={tab.key} value={tab.key}>
          {tab.key === portfolioTabs.transactions ? (
            <MyTransactions
              onRefresh={walletViewsEnabled ? refresh : undefined}
              isRefreshing={walletViewsEnabled ? isRefreshing : false}
            />
          ) : (
            tab.component && <tab.component />
          )}
        </Tabs.Panel>
      ))}
    </PortfolioRoot>
  );
};

export default Portfolio;
